import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from "../Context/CartContext";
import { OFFERS_IMAGES } from '../utils/imageUrls';

// Custom hook for intersection observer (נשאר ללא שינוי, בדיוק הלוגיקה שלך)
export const useIntersectionObserver = (options = {}) => {
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [element, setElement] = useState(null);

    useEffect(() => {
        if (!element) return;
        const observer = new IntersectionObserver(
            ([entry]) => setIsIntersecting(entry.isIntersecting),
            { threshold: 0.1, ...options }
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [element, options]);

    return [setElement, isIntersecting];
};

// Main projects data - עדכון של '---' ל'בהתאמה אישית' לשמירה על יוקרה
const projects = [
    {
        id: 21,
        title: 'חבילת פינות ישיבה',
        mainImage: OFFERS_IMAGES.packageTable,
        galleryImages: OFFERS_IMAGES.packageTableGallery,
        sizes: ['2 ספסלים זוגיים, שולחן פינת ישיבה ונדנדה'],
        woodTypes: ['אורן'],
        price: "בהתאמה אישית"
    },
    {
        id: 22,
        title: 'חבילת הגינה המשתלמת',
        mainImage: OFFERS_IMAGES.packageGarden,
        galleryImages: OFFERS_IMAGES.packageGardenGallery,
        sizes: ['ספסל זוגי, ספסל יחיד, שולחן פינת ישיבה'],
        woodTypes: ['אורן'],
        price: "בהתאמה אישית"
    }
];

export { projects };

// Package Card Component - מעוצב מחדש לשפת Organic Luxury
const PackageCard = React.memo(({
                                    project,
                                    index,
                                    onClick,
                                    isVisible
                                }) => (
    <article
        onClick={() => onClick(project)}
        className={`
            group relative overflow-hidden cursor-pointer flex flex-col
            bg-surface rounded-2xl shadow-soft hover:shadow-hover
            border border-secondary/50 hover:border-accent/40
            transform transition-all duration-700 ease-out
            ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}
        `}
        style={{ transitionDelay: `${index * 150}ms` }}
        role="button"
        tabIndex={0}
        aria-label={`הצג פרטי ${project.title}`}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(project);
            }
        }}
    >
        {/* תמונה עליונה (Aspect Ratio רחב לקטלוג) */}
        <div className="relative aspect-[4/3] sm:aspect-[16/9] overflow-hidden bg-secondary">
            <img
                src={project.mainImage}
                alt={project.title}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                loading="lazy"
            />
            {/* פילטר כהות עדין מופיע בהובר */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />

            {/* תגית מרחפת על התמונה - "מארז מיוחד" */}
            <div className="absolute top-4 right-4 bg-primary text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-md">
                מארז מיוחד
            </div>
        </div>

        {/* אזור הטקסט (Editorial Style) */}
        <div className="p-6 sm:p-8 flex flex-col flex-grow text-center relative z-10">
            <h3 className="text-2xl sm:text-3xl font-medium text-text-primary mb-3">
                {project.title}
            </h3>

            <p className="text-sm sm:text-base text-text-secondary font-light mb-6 flex-grow">
                הצעה משתלמת להלבשת הגינה שלכם בריהוט עץ תואם ואיכותי.
            </p>

            {/* פרטי החבילה (רשימה אדריכלית נקייה) */}
            <div className="space-y-2 mb-8 text-sm">
                <div className="flex items-center justify-center gap-2 text-text-primary">
                    <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                    <span>{project.sizes[0]}</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-text-primary">
                    <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                    <span>בנייה מעץ {project.woodTypes[0]} מלא</span>
                </div>
            </div>

            {/* קו חלוקה וכפתור משתמע */}
            <div className="pt-6 border-t border-secondary mt-auto">
                <span className="inline-flex items-center gap-2 text-primary font-medium group-hover:text-accent transition-colors duration-300">
                    לפרטים המלאים
                    <span className="transform transition-transform duration-300 group-hover:-translate-x-1">←</span>
                </span>
            </div>
        </div>
    </article>
));

const Offers = () => {
    const { addToItem } = useContext(CartContext);
    const navigate = useNavigate();
    const [headerRef, headerVisible] = useIntersectionObserver();
    const [cardsRef, cardsVisible] = useIntersectionObserver();

    const handleClick = React.useCallback((project) => {
        addToItem(project);
        navigate(`/product-page/${project.id}`, { state: { project } });
    }, [addToItem, navigate]);

    return (
        <section className="py-24 bg-background border-t border-secondary/50" dir="rtl">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* כותרת הסקשן */}
                <div
                    ref={headerRef}
                    className={`text-center mb-16 transform transition-all duration-1000 ease-out ${headerVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
                >
                    <h2 className="text-sm font-bold tracking-widest text-accent uppercase mb-4">
                        מארזים מיוחדים
                    </h2>
                    <h1 className="text-4xl md:text-5xl font-medium text-text-primary mb-6">
                        החבילות שלנו
                    </h1>
                    <div className="w-16 h-1 bg-accent mx-auto rounded-full mb-6"></div>
                    <p className="text-lg text-text-secondary max-w-2xl mx-auto font-light">
                        עיצבנו עבורכם סטים מושלמים שיחסכו לכם התלבטויות ויהפכו את הגינה למקום שכיף לארח בו.
                    </p>
                </div>

                {/* גריד הכרטיסיות */}
                <div
                    ref={cardsRef}
                    className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto"
                >
                    {projects.map((project, index) => (
                        <PackageCard
                            key={project.id}
                            project={project}
                            index={index}
                            onClick={handleClick}
                            isVisible={cardsVisible}
                        />
                    ))}
                </div>

            </div>
        </section>
    );
};

export default Offers;